import { isGiftOfferProduct, PurchaseItemStatus } from "common";
import { concat } from "lodash";
import { useMemo } from "react";
import {
    Affiliate,
    BookingEngineNamedSource,
    DelegateRole,
    LineItemDisputeStatus,
    PurchaseItemObject,
    Source,
} from "types";
import shallow from "zustand/shallow";
import { CriteriaItemProps } from "../components/Dashboard/Filters/ListFilter";
import { DateType, defaultFilter, usePurchaseFilterStore } from "../stores/purchaseFilterStore";
import { ResourceSelectionState } from "../types/Dashboard";
import { useDeletedExperiences } from "./resources/useDeletedExperiences";
import { useProducts } from "./resources/useProduct";
import { useAutoFetchCollection } from "./useAutoFetchCollection";
import { useInstalledPlugins } from "./usePlugins";

export interface PurchaseFilterField {
    name: string;
    id: ID | ID[];
}

export interface PurchaseFilterItem extends Pick<CriteriaItemProps, "label" | "localize" | "show"> {
    value: ResourceSelectionState;
    onReset: () => void;
    fetch: () => { data: PurchaseFilterField[] };
    onChange: (type: ResourceSelectionState, showUnassigned: boolean) => void;
    data: PurchaseFilterField[];
    unassignedLabel?: string;
}

export const useGetAllProducts = () => {
    const { products: activeProducts } = useProducts();
    const { deletedExperiences } = useDeletedExperiences();

    const products = useMemo(() => {
        return concat(activeProducts, deletedExperiences);
    }, [activeProducts, deletedExperiences]);

    return products.map((product) => {
        const value = { id: product.id, name: product.name };
        if (isGiftOfferProduct(product)) {
            value.name += " Gift";
        }

        return value;
    });
};

export const typeFields = [
    {
        name: "Experiences",
        id: PurchaseItemObject.EXPERIENCE,
    },
    {
        name: "Merchandise",
        id: PurchaseItemObject.WARE,
    },
    {
        name: "Gifts",
        id: PurchaseItemObject.GIFT,
    },
    {
        name: "Store Credits",
        id: PurchaseItemObject.STORE_CREDIT,
    },
    {
        name: "Memberships",
        id: PurchaseItemObject.MEMBERSHIP,
    },
];

export const disputeFields = [
    {
        name: "Open",
        id: LineItemDisputeStatus.PENDING,
    },
    {
        name: "Won",
        id: LineItemDisputeStatus.WON,
    },
    {
        name: "Lost",
        id: LineItemDisputeStatus.LOST,
    },
];

export const dateFields = [
    {
        name: "Purchase Date",
        id: DateType.PURCHASE_DATE,
    },
    {
        name: "Arrival Date",
        id: DateType.ARRIVAL_DATE,
    },
];

export const statusFields = [
    {
        name: "Confirmed",
        id: PurchaseItemStatus.CONFIRMED,
    },
    {
        name: "Pending",
        id: [PurchaseItemStatus.PENDING_ACCEPT, PurchaseItemStatus.PENDING_MINIMUM],
    },
    {
        name: "Cancelled",
        id: PurchaseItemStatus.CANCELED,
    },
];

export const balanceFields = [
    {
        name: "Balance Due",
        id: "due",
    },
    {
        name: "Paid in Full",
        id: "paid",
    },
    {
        name: "Refund Due",
        id: "refund",
    },
];

export const useSourceFields = () => {
    const { data: plugins } = useInstalledPlugins();
    const pluginIds = plugins
        .filter((plugin) => plugin.permissions.includes(DelegateRole.BOOKING_ENGINE))
        .map((plugin) => plugin.id);
    return [
        {
            name: "Checkout",
            id: Source.CHECKOUT,
        },
        {
            name: "Back Office",
            id: Source.OFFICE,
        },
        {
            name: "Kiosk",
            id: Source.KIOSK,
        },
        {
            name: "Apps & Integrations",
            id: [BookingEngineNamedSource.EXPEDIA, BookingEngineNamedSource.VIATOR, ...pluginIds],
        },
    ];
};

export const usePurchaseFilters = () => {
    const [filters, setFilters] = usePurchaseFilterStore((state) => [state.filters, state.setFilters], shallow);

    const productFields = useGetAllProducts();
    const { data: affiliateFields } = useAutoFetchCollection<Affiliate>(["affiliates"]);
    const sourceFields = useSourceFields();

    const FiltersList: PurchaseFilterItem[] = [
        {
            label: "Type",
            value: filters.type,
            onReset: () => setFilters({ type: defaultFilter }),
            fetch: () => ({ data: typeFields }),
            onChange: (type: ResourceSelectionState) => setFilters({ type }),
            show: { search: false },
            localize: true,
            data: typeFields,
        },
        {
            label: "Product",
            value: filters.product,
            localize: false,
            onReset: () => setFilters({ product: defaultFilter }),
            fetch: () => ({ data: productFields }),
            onChange: (product: ResourceSelectionState) => setFilters({ product }),
            show: { search: true },
            data: productFields,
        },
        {
            label: "Status",
            value: filters.status,
            onReset: () => setFilters({ status: defaultFilter }),
            fetch: () => ({ data: statusFields }),
            onChange: (status: ResourceSelectionState) => setFilters({ status }),
            show: { search: false },
            localize: true,
            data: statusFields,
        },
        {
            label: "Balance",
            value: filters.balance,
            onReset: () => setFilters({ balance: defaultFilter }),
            fetch: () => ({ data: balanceFields }),
            onChange: (balance: ResourceSelectionState) => setFilters({ balance }),
            show: { search: false },
            localize: true,
            data: balanceFields,
        },
        {
            label: "Dispute",
            value: filters.dispute,
            onReset: () => setFilters({ dispute: { ...defaultFilter, showUnassigned: true } }),
            fetch: () => ({ data: disputeFields }),
            onChange: (dispute: ResourceSelectionState, showUnassigned: boolean) =>
                setFilters({
                    dispute: {
                        ...dispute,
                        showUnassigned: dispute.selected.length === 0 ? showUnassigned : false,
                    },
                }),
            unassignedLabel: "purchases",
            localize: true,
            show: { search: false, unassignedCheckbox: true },
            data: disputeFields,
        },
        {
            label: "Source",
            value: filters.source,
            onReset: () => setFilters({ source: defaultFilter }),
            fetch: () => ({ data: sourceFields }),
            onChange: (source: ResourceSelectionState) => setFilters({ source }),
            show: { search: false },
            localize: true,
            data: sourceFields,
        },
        {
            label: "Agent",
            value: filters.affiliate,
            localize: false,
            onReset: () => setFilters({ affiliate: { ...defaultFilter, showUnassigned: true } }),
            fetch: () => ({ data: affiliateFields }),
            onChange: (affiliate: ResourceSelectionState, showUnassigned: boolean) =>
                setFilters({
                    affiliate: {
                        ...affiliate,
                        showUnassigned: affiliate.selected.length === 0 ? showUnassigned : false,
                    },
                }),
            unassignedLabel: "purchases",
            show: { search: false, unassignedCheckbox: true },
            data: affiliateFields,
        },
    ];

    return FiltersList;
};
